import React, { useState, useEffect } from 'react';
import './Promociones.css';
import Header from '../common/Header';
import Footer from '../common/Footer';
import FormModal from '../common/FormModal';
import ConfirmModal from '../common/ConfirmModal';
import FormField from '../common/FormField';
import FormGrid, { FormGridSection } from '../common/FormGrid';
import { useToast } from '../../contexts/ToastContext';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaTimes,
  FaTag,
  FaPercent,
  FaGift
} from 'react-icons/fa';
import { getApiUrl } from '../../utils/api';

const DIAS_SEMANA = [
  { valor: 2, nombre: 'Lun' },
  { valor: 3, nombre: 'Mar' },
  { valor: 4, nombre: 'Mie' },
  { valor: 5, nombre: 'Jue' },
  { valor: 6, nombre: 'Vie' },
  { valor: 7, nombre: 'Sab' },
  { valor: 1, nombre: 'Dom' }
];

const TIPOS_PEDIDO = [
  { campo: 'mesa', nombre: 'Mesa' },
  { campo: 'llevar', nombre: 'Llevar' },
  { campo: 'domicilio', nombre: 'Domicilio' },
  { campo: 'uber', nombre: 'Uber' },
  { campo: 'rappi', nombre: 'Rappi' },
  { campo: 'delibot', nombre: 'Delibot' }
];

const formularioInicial = {
  nombre: '',
  tipoPromocion: '1',
  activar: 1,
  fechaInicio: '',
  fechaFin: '',
  horaInicio: '00:00',
  horaFin: '23:59',
  descuento: 0,
  cantidad1: 2,
  cantidad2: 1,
  mesa: 1,
  llevar: 0,
  domicilio: 0,
  uber: 0,
  rappi: 0,
  delibot: 0,
  diasSeleccionados: [1, 2, 3, 4, 5, 6, 7],
  platillosSeleccionados: [],
  productosCobrar: [],
  productosRegalar: []
};

// ─── Componente extraído para evitar re-mount y pérdida de focus ─────
const SelectorPlatillos = ({ lista, busquedaState, setBusquedaState, label, formulario, productos, onAgregar, onQuitar, onLimpiar, getNombrePlatillo }) => {
  const filtrarProductos = (texto) => {
    if (!texto) return [];
    return productos.filter(p =>
      p.nombre?.toLowerCase().includes(texto.toLowerCase())
    ).slice(0, 15);
  };

  const resultados = filtrarProductos(busquedaState);
  const seleccionados = formulario[lista] || [];

  return (
    <div className="promo-platillos-selector">
      <div className="promo-platillos-search">
        <FaSearch className="promo-platillos-search-icon" />
        <input
          type="text"
          value={busquedaState}
          onChange={(e) => setBusquedaState(e.target.value)}
          placeholder={`Buscar platillo para ${label}...`}
          className="promo-platillos-search-input"
        />
        {busquedaState && (
          <button onClick={() => setBusquedaState('')} className="promo-platillos-clear">
            <FaTimes />
          </button>
        )}
      </div>
      {busquedaState && resultados.length > 0 && (
        <div className="promo-platillos-dropdown">
          {resultados.map(p => {
            const id = p.idProducto || p.idPlatillo;
            const yaSeleccionado = seleccionados.includes(id);
            return (
              <div
                key={id}
                className={`promo-platillos-option ${yaSeleccionado ? 'promo-platillos-option--selected' : ''}`}
                onClick={() => {
                  if (!yaSeleccionado) {
                    onAgregar(id, lista);
                    setBusquedaState('');
                  }
                }}
              >
                {p.nombre} {yaSeleccionado && '(ya agregado)'}
              </div>
            );
          })}
        </div>
      )}
      {seleccionados.length > 0 && (
        <div className="promo-platillos-list">
          {seleccionados.map(id => (
            <span key={id} className="promo-platillo-chip">
              {getNombrePlatillo(id)}
              <button onClick={() => onQuitar(id, lista)} className="promo-platillo-chip-remove">
                <FaTimes />
              </button>
            </span>
          ))}
          <button onClick={() => onLimpiar(lista)} className="promo-platillos-clear-all">
            Limpiar todos
          </button>
        </div>
      )}
    </div>
  );
};

const Promociones = () => {
  const [promociones, setPromociones] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [promocionSeleccionada, setPromocionSeleccionada] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [promoAEliminar, setPromoAEliminar] = useState(null);
  const [formulario, setFormulario] = useState({ ...formularioInicial });
  const [busquedaPlatillo, setBusquedaPlatillo] = useState('');
  const [busquedaCobrar, setBusquedaCobrar] = useState('');
  const [busquedaRegalar, setBusquedaRegalar] = useState('');
  const toast = useToast();

  useEffect(() => {
    cargarPromociones();
    cargarProductos();
  }, []);

  // ─── API CALLS ──────────────────────────────────────────────────────
  const cargarPromociones = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getTodasLasPromociones' })
      });
      const data = await response.json();
      if (data.success) {
        setPromociones(data.data || []);
      } else {
        toast.error(data.message || 'Error al cargar promociones');
      }
    } catch (error) {
      console.error('Error al cargar promociones:', error);
      toast.error('Error al cargar las promociones');
    } finally {
      setLoading(false);
    }
  };

  const cargarProductos = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getTodosLosPlatillos' })
      });
      const data = await response.json();
      if (data.success) {
        setProductos(data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
    }
  };

  // ─── CRUD ───────────────────────────────────────────────────────────
  const nuevaPromocion = () => {
    setPromocionSeleccionada(null);
    setFormulario({ ...formularioInicial });
    setBusquedaPlatillo('');
    setBusquedaCobrar('');
    setBusquedaRegalar('');
    setShowModal(true);
  };

  const editarPromocion = async (promo) => {
    setPromocionSeleccionada(promo);

    let productosCobrar = [];
    let productosRegalar = [];

    if (promo.tipo === '3' || promo.tipo === 3) {
      try {
        const response = await fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getProductosPromocionRegalo', idPromocion: promo.idPromocion })
        });
        const data = await response.json();
        if (data.success && data.data) {
          productosCobrar = data.data.productosCobrar || [];
          productosRegalar = data.data.productosRegalar || [];
        }
      } catch (e) {
        console.error('Error cargando productos regalo:', e);
      }
    }

    setFormulario({
      nombre: promo.nombre || '',
      tipoPromocion: String(promo.tipo || '1'),
      activar: promo.activar || 0,
      fechaInicio: promo.fechaInicio || '',
      fechaFin: promo.fechaFin || '',
      horaInicio: (promo.horaInicio || '00:00:00').substring(0, 5),
      horaFin: (promo.horaFin || '23:59:59').substring(0, 5),
      descuento: promo.descuento || 0,
      cantidad1: promo.cantidad1 || 2,
      cantidad2: promo.cantidad2 || 1,
      mesa: promo.mesa || 0,
      llevar: promo.llevar || 0,
      domicilio: promo.domicilio || 0,
      uber: promo.uber || 0,
      rappi: promo.rappi || 0,
      delibot: promo.delibot || 0,
      diasSeleccionados: promo.diasConfigurados || [1, 2, 3, 4, 5, 6, 7],
      platillosSeleccionados: promo.aplicaProductos || [],
      productosCobrar,
      productosRegalar
    });
    setBusquedaPlatillo('');
    setBusquedaCobrar('');
    setBusquedaRegalar('');
    setShowModal(true);
  };

  const guardarPromocion = async () => {
    if (!formulario.nombre.trim()) {
      toast.error('El nombre de la promoción es requerido');
      return;
    }
    if (!formulario.fechaInicio || !formulario.fechaFin) {
      toast.error('Las fechas de inicio y fin son requeridas');
      return;
    }

    try {
      setGuardando(true);
      const esEdicion = !!promocionSeleccionada;
      const payload = {
        tipo: esEdicion ? 'actualizarPromocion' : 'crearPromocion',
        nombre: formulario.nombre,
        tipoPromocion: formulario.tipoPromocion,
        activar: formulario.activar,
        fechaInicio: formulario.fechaInicio,
        fechaFin: formulario.fechaFin,
        horaInicio: formulario.horaInicio + ':00',
        horaFin: formulario.horaFin + ':59',
        descuento: parseFloat(formulario.descuento) || 0,
        cantidad1: parseInt(formulario.cantidad1) || 0,
        cantidad2: parseInt(formulario.cantidad2) || 0,
        mesa: formulario.mesa,
        llevar: formulario.llevar,
        domicilio: formulario.domicilio,
        uber: formulario.uber,
        rappi: formulario.rappi,
        delibot: formulario.delibot,
        platillosSeleccionados: formulario.platillosSeleccionados,
        diasSeleccionados: formulario.diasSeleccionados,
        productosCobrar: formulario.productosCobrar,
        productosRegalar: formulario.productosRegalar
      };

      if (esEdicion) {
        payload.idPromocion = promocionSeleccionada.idPromocion;
      }

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        toast.success(esEdicion ? 'Promoción actualizada correctamente' : 'Promoción creada correctamente');
        handleCloseModal();
        cargarPromociones();
      } else {
        toast.error(data.message || 'Error al guardar la promoción');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar la promoción');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = (promo) => {
    setPromoAEliminar(promo);
    setShowDeleteModal(true);
  };

  const eliminarPromocion = async () => {
    if (!promoAEliminar) return;
    try {
      setGuardando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'eliminarPromocion', idPromocion: promoAEliminar.idPromocion })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Promoción eliminada correctamente');
        setShowDeleteModal(false);
        setPromoAEliminar(null);
        setPromocionSeleccionada(null);
        cargarPromociones();
      } else {
        toast.error(data.message || 'Error al eliminar la promoción');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar la promoción');
    } finally {
      setGuardando(false);
    }
  };

  const toggleActivar = async (promo) => {
    const nuevoEstado = promo.activar ? 0 : 1;
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'toggleActivarPromocion', idPromocion: promo.idPromocion, activar: nuevoEstado })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        cargarPromociones();
      } else {
        toast.error(data.message || 'Error al cambiar estado');
      }
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setPromocionSeleccionada(null);
    setFormulario({ ...formularioInicial });
    setBusquedaPlatillo('');
    setBusquedaCobrar('');
    setBusquedaRegalar('');
  };

  // ─── FORM HANDLERS ──────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormulario(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const toggleDia = (dia) => {
    setFormulario(prev => {
      const seleccionados = prev.diasSeleccionados.includes(dia)
        ? prev.diasSeleccionados.filter(d => d !== dia)
        : [...prev.diasSeleccionados, dia];
      return { ...prev, diasSeleccionados: seleccionados };
    });
  };

  const toggleTodosDias = () => {
    const todos = [1, 2, 3, 4, 5, 6, 7];
    setFormulario(prev => ({
      ...prev,
      diasSeleccionados: prev.diasSeleccionados.length === 7 ? [] : todos
    }));
  };

  const toggleTipoPedido = (campo) => {
    setFormulario(prev => ({
      ...prev,
      [campo]: prev[campo] ? 0 : 1
    }));
  };

  const agregarPlatillo = (idPlatillo, lista) => {
    setFormulario(prev => {
      if (prev[lista].includes(idPlatillo)) return prev;
      return { ...prev, [lista]: [...prev[lista], idPlatillo] };
    });
  };

  const quitarPlatillo = (idPlatillo, lista) => {
    setFormulario(prev => ({
      ...prev,
      [lista]: prev[lista].filter(id => id !== idPlatillo)
    }));
  };

  const limpiarPlatillos = (lista) => {
    setFormulario(prev => ({ ...prev, [lista]: [] }));
  };

  const getNombrePlatillo = (id) => {
    const p = productos.find(p => p.idProducto === id || p.idPlatillo === id);
    return p ? p.nombre : `Producto #${id}`;
  };

  // ─── FILTROS ────────────────────────────────────────────────────────
  const promocionesFiltradas = promociones.filter(p => {
    if (!busqueda) return true;
    const texto = busqueda.toLowerCase();
    return p.nombre?.toLowerCase().includes(texto);
  });

  // ─── HELPERS DE DISPLAY ─────────────────────────────────────────────
  const getTipoBadge = (tipo) => {
    switch (String(tipo)) {
      case '1': return { label: '% Descuento', clase: 'promo-tipo-badge--t1' };
      case '2': return { label: 'NxM', clase: 'promo-tipo-badge--t2' };
      case '3': return { label: 'Combo', clase: 'promo-tipo-badge--t3' };
      default: return { label: tipo, clase: '' };
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    const [y, m, d] = fecha.split('-');
    return `${d}/${m}/${y}`;
  };

  // Props comunes para SelectorPlatillos
  const selectorProps = {
    formulario,
    productos,
    onAgregar: agregarPlatillo,
    onQuitar: quitarPlatillo,
    onLimpiar: limpiarPlatillos,
    getNombrePlatillo
  };

  // ─── RENDER ─────────────────────────────────────────────────────────
  return (
    <div className="promociones-container">
      <Header titulo="Promociones" color="#CDDC39" />

      <div className="promo-excel">
        {/* Header con búsqueda y acciones */}
        <div className="excel-header" style={{ background: 'linear-gradient(135deg, #CDDC39 0%, #AFB42B 100%)' }}>
          <div className="excel-header-controls">
            <div className="excel-search-container">
              <div className="excel-search-group">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar promociones..."
                  className="excel-search-input"
                />
                {busqueda && (
                  <button onClick={() => setBusqueda('')} className="excel-clear-search">
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
            <div className="excel-acciones">
              <button onClick={nuevaPromocion} className="btn-excel btn-nuevo">
                <FaPlus /> Nueva Promoción
              </button>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="excel-table-container">
          <table className="excel-table">
            <thead>
              <tr>
                <th className="excel-th">Nombre</th>
                <th className="excel-th">Tipo</th>
                <th className="excel-th">Vigencia</th>
                <th className="excel-th">Aplica a</th>
                <th className="excel-th" style={{ textAlign: 'center' }}>Activa</th>
                <th className="excel-th excel-th-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="excel-loading">Cargando promociones...</td>
                </tr>
              ) : promocionesFiltradas.length === 0 ? (
                <tr>
                  <td colSpan="6" className="excel-empty">
                    {busqueda ? `No se encontraron promociones que coincidan con "${busqueda}"` : 'No hay promociones registradas'}
                  </td>
                </tr>
              ) : (
                promocionesFiltradas.map(promo => {
                  const tipoBadge = getTipoBadge(promo.tipo);
                  return (
                    <tr
                      key={promo.idPromocion}
                      className={`excel-row ${promocionSeleccionada?.idPromocion === promo.idPromocion ? 'excel-row-selected' : ''}`}
                      onClick={() => setPromocionSeleccionada(promo)}
                    >
                      <td className="excel-cell excel-cell-nombre">{promo.nombre}</td>
                      <td className="excel-cell">
                        <span className={`promo-tipo-badge ${tipoBadge.clase}`}>{tipoBadge.label}</span>
                      </td>
                      <td className="excel-cell">
                        {formatearFecha(promo.fechaInicio)} - {formatearFecha(promo.fechaFin)}
                      </td>
                      <td className="excel-cell">
                        <div className="promo-aplica-badges">
                          {TIPOS_PEDIDO.filter(tp => promo[tp.campo]).map(tp => (
                            <span key={tp.campo} className="promo-aplica-badge">{tp.nombre}</span>
                          ))}
                        </div>
                      </td>
                      <td className="excel-cell" style={{ textAlign: 'center' }}>
                        <button
                          className={`promo-toggle ${promo.activar ? 'promo-toggle--on' : 'promo-toggle--off'}`}
                          onClick={(e) => { e.stopPropagation(); toggleActivar(promo); }}
                          title={promo.activar ? 'Desactivar' : 'Activar'}
                        >
                          <span className="promo-toggle-slider" />
                          <span className="promo-toggle-label">{promo.activar ? 'SÍ' : 'NO'}</span>
                        </button>
                      </td>
                      <td className="excel-cell excel-cell-actions">
                        <button
                          onClick={(e) => { e.stopPropagation(); editarPromocion(promo); }}
                          className="excel-btn-action excel-btn-edit"
                          title="Editar"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); confirmarEliminar(promo); }}
                          className="excel-btn-action excel-btn-delete"
                          title="Eliminar"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="excel-footer">
          <div className="excel-info">
            <span>Total: <strong>{promociones.length}</strong></span>
            {busqueda && <span>| Filtradas: <strong>{promocionesFiltradas.length}</strong></span>}
          </div>
          <div className="excel-shortcuts">
            <span>Click para seleccionar | Editar | Eliminar</span>
          </div>
        </div>
      </div>

      {/* Modal Crear/Editar - 2 columnas */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={promocionSeleccionada ? 'Editar Promoción' : 'Nueva Promoción'}
        onSave={guardarPromocion}
        saveLabel={promocionSeleccionada ? 'Actualizar' : 'Crear'}
        isSaving={guardando}
        size="xlarge"
        saveIcon={<FaTag />}
        className="promo-modal"
      >
        <div className="promo-modal-grid">
          {/* ═══ COLUMNA IZQUIERDA: Datos básicos + Vigencia ═══ */}
          <div className="promo-modal-col">
            <FormGridSection title="Datos básicos">
              <FormGrid columns={2} gap="10px">
                <FormField
                  label="Nombre"
                  name="nombre"
                  value={formulario.nombre}
                  onChange={handleInputChange}
                  placeholder="Ej: 2x1 en pizzas"
                  required
                  icon={<FaTag />}
                />
                <FormField
                  label="Estado"
                  type="select"
                  name="activar"
                  value={formulario.activar}
                  onChange={(e) => setFormulario(prev => ({ ...prev, activar: parseInt(e.target.value) }))}
                  options={[
                    { value: 1, label: 'Activa' },
                    { value: 0, label: 'Inactiva' }
                  ]}
                />
              </FormGrid>
            </FormGridSection>

            <FormGridSection title="Vigencia">
              <FormGrid columns={2} gap="10px">
                <FormField
                  label="Fecha inicio"
                  type="date"
                  name="fechaInicio"
                  value={formulario.fechaInicio}
                  onChange={handleInputChange}
                  required
                />
                <FormField
                  label="Fecha fin"
                  type="date"
                  name="fechaFin"
                  value={formulario.fechaFin}
                  onChange={handleInputChange}
                  required
                />
                <FormField
                  label="Hora inicio"
                  type="text"
                  name="horaInicio"
                  value={formulario.horaInicio}
                  onChange={handleInputChange}
                  placeholder="00:00"
                />
                <FormField
                  label="Hora fin"
                  type="text"
                  name="horaFin"
                  value={formulario.horaFin}
                  onChange={handleInputChange}
                  placeholder="23:59"
                />
              </FormGrid>
            </FormGridSection>
          </div>

          {/* ═══ COLUMNA DERECHA: Tipo, Pedido, Días, Platillos ═══ */}
          <div className="promo-modal-col">
            <FormGridSection title="Tipo de promoción">
              <div className="promo-tipo-selector">
                <label className={`promo-tipo-option ${formulario.tipoPromocion === '1' ? 'promo-tipo-option--active' : ''}`}>
                  <input
                    type="radio"
                    name="tipoPromocion"
                    value="1"
                    checked={formulario.tipoPromocion === '1'}
                    onChange={handleInputChange}
                  />
                  <FaPercent className="promo-tipo-icon" />
                  <span>% Descuento</span>
                </label>
                <label className={`promo-tipo-option ${formulario.tipoPromocion === '2' ? 'promo-tipo-option--active' : ''}`}>
                  <input
                    type="radio"
                    name="tipoPromocion"
                    value="2"
                    checked={formulario.tipoPromocion === '2'}
                    onChange={handleInputChange}
                  />
                  <FaTag className="promo-tipo-icon" />
                  <span>NxM</span>
                </label>
                <label className={`promo-tipo-option ${formulario.tipoPromocion === '3' ? 'promo-tipo-option--active' : ''}`}>
                  <input
                    type="radio"
                    name="tipoPromocion"
                    value="3"
                    checked={formulario.tipoPromocion === '3'}
                    onChange={handleInputChange}
                  />
                  <FaGift className="promo-tipo-icon" />
                  <span>Combo/Regalo</span>
                </label>
              </div>

              <div className="promo-tipo-config">
                {formulario.tipoPromocion === '1' && (
                  <div className="promo-config-row">
                    <label>Descuento:</label>
                    <input
                      type="number"
                      name="descuento"
                      value={formulario.descuento}
                      onChange={handleInputChange}
                      className="promo-config-input"
                      min="0"
                      max="1"
                      step="0.01"
                    />
                    <span className="promo-config-hint">(0.20 = 20%)</span>
                  </div>
                )}
                {formulario.tipoPromocion === '2' && (
                  <div className="promo-config-nxm">
                    <div className="promo-config-row">
                      <label>Compra:</label>
                      <input
                        type="number"
                        name="cantidad1"
                        value={formulario.cantidad1}
                        onChange={handleInputChange}
                        className="promo-config-input"
                        min="1"
                      />
                      <label>Paga:</label>
                      <input
                        type="number"
                        name="cantidad2"
                        value={formulario.cantidad2}
                        onChange={handleInputChange}
                        className="promo-config-input"
                        min="0"
                      />
                    </div>
                    <div className="promo-config-row">
                      <label>Descuento en extra:</label>
                      <input
                        type="number"
                        name="descuento"
                        value={formulario.descuento}
                        onChange={handleInputChange}
                        className="promo-config-input"
                        min="0"
                        max="1"
                        step="0.01"
                      />
                      <span className="promo-config-hint">(1 = 100% gratis)</span>
                    </div>
                  </div>
                )}
                {formulario.tipoPromocion === '3' && (
                  <div className="promo-config-combo">
                    <div className="promo-seccion">
                      <h4>Productos que el cliente compra (cobrar)</h4>
                      <div className="promo-config-row">
                        <label>Cantidad a cobrar:</label>
                        <input
                          type="number"
                          name="cantidad1"
                          value={formulario.cantidad1}
                          onChange={handleInputChange}
                          className="promo-config-input"
                          min="1"
                        />
                      </div>
                      <SelectorPlatillos
                        lista="productosCobrar"
                        busquedaState={busquedaCobrar}
                        setBusquedaState={setBusquedaCobrar}
                        label="cobrar"
                        {...selectorProps}
                      />
                    </div>
                    <div className="promo-seccion">
                      <h4>Productos de regalo (gratis)</h4>
                      <div className="promo-config-row">
                        <label>Cantidad a regalar:</label>
                        <input
                          type="number"
                          name="cantidad2"
                          value={formulario.cantidad2}
                          onChange={handleInputChange}
                          className="promo-config-input"
                          min="1"
                        />
                      </div>
                      <SelectorPlatillos
                        lista="productosRegalar"
                        busquedaState={busquedaRegalar}
                        setBusquedaState={setBusquedaRegalar}
                        label="regalar"
                        {...selectorProps}
                      />
                    </div>
                  </div>
                )}
              </div>
            </FormGridSection>

            <FormGridSection title="Aplica a tipo de pedido">
              <div className="promo-tipo-pedido-grid">
                {TIPOS_PEDIDO.map(tp => (
                  <label key={tp.campo} className={`promo-tipo-pedido-item ${formulario[tp.campo] ? 'promo-tipo-pedido-item--active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={!!formulario[tp.campo]}
                      onChange={() => toggleTipoPedido(tp.campo)}
                    />
                    <span>{tp.nombre}</span>
                  </label>
                ))}
              </div>
            </FormGridSection>

            <FormGridSection title="Días de la semana">
              <div className="promo-dias-container">
                <div className="promo-dias-grid">
                  {DIAS_SEMANA.map(d => (
                    <label key={d.valor} className={`promo-dia-item ${formulario.diasSeleccionados.includes(d.valor) ? 'promo-dia-item--active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={formulario.diasSeleccionados.includes(d.valor)}
                        onChange={() => toggleDia(d.valor)}
                      />
                      <span>{d.nombre}</span>
                    </label>
                  ))}
                </div>
                <button onClick={toggleTodosDias} className="promo-dias-toggle-btn">
                  {formulario.diasSeleccionados.length === 7 ? 'Ninguno' : 'Todos'}
                </button>
              </div>
            </FormGridSection>

            {formulario.tipoPromocion !== '3' && (
              <FormGridSection title={`Platillos aplicables (${formulario.platillosSeleccionados.length})`}>
                <SelectorPlatillos
                  lista="platillosSeleccionados"
                  busquedaState={busquedaPlatillo}
                  setBusquedaState={setBusquedaPlatillo}
                  label="agregar"
                  {...selectorProps}
                />
              </FormGridSection>
            )}
          </div>
        </div>
      </FormModal>

      {/* Modal confirmar eliminar */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setPromoAEliminar(null); }}
        onConfirm={eliminarPromocion}
        title="Eliminar Promoción"
        message={`¿Está seguro de eliminar la promoción "${promoAEliminar?.nombre}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        type="danger"
        isLoading={guardando}
      />

      <Footer />
    </div>
  );
};

export default Promociones;
